import { useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { EMPLOYEE_NAV_CHILDREN } from './employeeNav';
import { canUsePayrollRecords } from '../hrms/pages/Payroll/payrollAccess';
import { canManageWorkflow } from '../hrms/pages/Workflow/workflowAccess';

const MIN_WIDTH = 168;
const MAX_WIDTH = 320;
const DEFAULT_WIDTH = 198;
const STORAGE_KEY = 'teamlio:employeeNavWidth';

function readStoredWidth(): number {
  try {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(saved) && saved >= MIN_WIDTH && saved <= MAX_WIDTH) return saved;
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_WIDTH;
}

export default function EmployeeNavPanel() {
  const { user } = useAuth();
  const { hasAccess, isPathVisible, canManage } = useAccess();
  const location = useLocation();
  const [width, setWidth] = useState<number>(readStoredWidth);
  const draggingRef = useRef(false);
  const canShowPayrollChildren = canUsePayrollRecords(user?.role, { hasAccess, canManage });

  const items = EMPLOYEE_NAV_CHILDREN.filter((c) => !hasAccess || isPathVisible(c.to));
  if (items.length === 0) return null;

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    if (draggingRef.current) return;
    draggingRef.current = true;
    const startX = e.clientX;
    const startW = width;
    let finalW = startW;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      finalW = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + (ev.clientX - startX)));
      setWidth(finalW);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try {
        localStorage.setItem(STORAGE_KEY, String(Math.round(finalW)));
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const resetWidth = () => {
    setWidth(DEFAULT_WIDTH);
    try {
      localStorage.setItem(STORAGE_KEY, String(DEFAULT_WIDTH));
    } catch {
      /* ignore */
    }
  };

  return (
    <aside
      className="gt-sidebar gt-task-panel"
      style={{ width, flexShrink: 0, position: 'relative' }}
    >
      <div className="gt-sidebar-header">
        <span className="gt-sidebar-brand">Employee Hub</span>
      </div>

      <nav className="gt-sidebar-nav">
        {items.map(({ to, label, Icon, children }) => {
          const isPayroll = to === '/employee/payroll';
          const canShowChildren = !isPayroll || canShowPayrollChildren;
          const visibleChildren = canShowChildren
            ? (children || []).filter((child) =>
                (!child.managerOnly || canManageWorkflow(user?.role)) &&
                (!hasAccess || isPathVisible(child.to))
              )
            : [];
          const childActive = visibleChildren.some((child) =>
            location.pathname === child.to || location.pathname.startsWith(child.to + '/')
          );
          const expanded = visibleChildren.length > 0 && childActive;

          return (
            <div key={to}>
              <NavLink
                to={to}
                className={({ isActive }) => `gt-sidebar-item${isActive || childActive ? ' is-active' : ''}`}
              >
                <Icon className="gt-sidebar-icon" />
                <span className="gt-sidebar-label">{label}</span>
                {visibleChildren.length > 0 ? (
                  <ChevronDown
                    className="ml-auto h-4 w-4 shrink-0"
                    style={{
                      color: 'var(--gt-text-tertiary)',
                      transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 120ms ease',
                    }}
                  />
                ) : null}
              </NavLink>

              {expanded ? (
                <div
                  className="ml-5 mt-1 flex flex-col gap-1 border-l pl-3"
                  style={{ borderColor: 'var(--gt-divider)' }}
                >
                  {visibleChildren.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      end={child.to === to}
                      className={({ isActive }) => `gt-sidebar-item${isActive ? ' is-active' : ''}`}
                      style={{
                        minHeight: 36,
                        padding: '0.45rem 0.75rem',
                        fontSize: 13,
                      }}
                    >
                      <span className="gt-sidebar-label">{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      <div
        className="gt-task-panel__resize"
        onMouseDown={startResize}
        onDoubleClick={resetWidth}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize employee hub panel"
        title="Drag to resize · double-click to reset"
        style={{
          position: 'absolute',
          top: 0,
          right: -3,
          width: 7,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 5,
        }}
      />
    </aside>
  );
}
